/**
 * MemoryRepository - In-memory implementation (per sviluppo e testing)
 *
 * Store tutto in RAM (volatile)
 * Utile per:
 * - Testing unitario
 * - Sviluppo senza persistenza
 * - Fallback quando storage non disponibile
 */

import type {
  IDataRepository,
  QuerySpec,
  PatchOperation,
  BatchOperation,
  BatchResult,
  RepositoryConfig,
  RepositoryMetadata,
  FilterCondition,
} from './types';

export class MemoryRepository implements IDataRepository {
  private store: Map<string, any> = new Map();
  private config?: RepositoryConfig;

  async init(config?: RepositoryConfig): Promise<void> {
    this.config = config;
    this.store.clear();
  }

  async get<T = any>(id: string): Promise<T | null> {
    return this.store.get(id) || null;
  }

  async query<T = any>(spec: QuerySpec): Promise<T[]> {
    let results = Array.from(this.store.values());

    // Apply where filters
    if (spec.where && spec.where.length > 0) {
      results = results.filter((record) => {
        return spec.where!.every((condition) => {
          return this.evaluateCondition(record, condition);
        });
      });
    }

    // Apply search (simple full-text)
    if (spec.search) {
      const searchLower = spec.search.toLowerCase();
      results = results.filter((record) => {
        const recordStr = JSON.stringify(record).toLowerCase();
        return recordStr.includes(searchLower);
      });
    }

    // Apply scope filter
    if (spec.scope) {
      results = results.filter((record) => record.scope === spec.scope);
    }

    // Apply orderBy
    if (spec.orderBy && spec.orderBy.length > 0) {
      results.sort((a, b) => {
        for (const order of spec.orderBy!) {
          const aVal = this.getNestedValue(a, order.field);
          const bVal = this.getNestedValue(b, order.field);

          if (aVal === bVal) continue;

          const comparison = aVal < bVal ? -1 : 1;
          return order.direction === 'asc' ? comparison : -comparison;
        }
        return 0;
      });
    }

    // Apply offset and limit
    if (spec.offset) {
      results = results.slice(spec.offset);
    }
    if (spec.limit) {
      results = results.slice(0, spec.limit);
    }

    return results;
  }

  async put<T = any>(record: T): Promise<T> {
    const id = (record as any).id;
    if (!id) {
      throw new Error('Record must have an id field');
    }

    // Add metadata if not present
    const now = new Date().toISOString();
    const enrichedRecord = {
      ...record,
      updatedAt: now,
      createdAt: (record as any).createdAt || now,
    };

    this.store.set(id, enrichedRecord);
    return enrichedRecord;
  }

  async patch<T = any>(id: string, ops: PatchOperation[]): Promise<T> {
    const record = await this.get(id);
    if (!record) {
      throw new Error(`Record with id ${id} not found`);
    }

    // Apply patch operations
    let updated = { ...record };
    for (const op of ops) {
      updated = this.applyPatchOp(updated, op);
    }

    // Update metadata
    updated.updatedAt = new Date().toISOString();

    this.store.set(id, updated);
    return updated;
  }

  async delete(id: string): Promise<void> {
    this.store.delete(id);
  }

  async batch(ops: BatchOperation[]): Promise<BatchResult> {
    const results: BatchResult['results'] = [];

    for (const op of ops) {
      try {
        switch (op.type) {
          case 'put':
            await this.put(op.record);
            results.push({ id: op.record.id, success: true });
            break;

          case 'patch':
            await this.patch(op.id, op.ops);
            results.push({ id: op.id, success: true });
            break;

          case 'delete':
            await this.delete(op.id);
            results.push({ id: op.id, success: true });
            break;
        }
      } catch (error) {
        results.push({
          id: op.type === 'delete' ? op.id : op.type === 'patch' ? op.id : op.record.id,
          success: false,
          error: (error as Error).message,
        });
      }
    }

    const success = results.every((r) => r.success);
    return { success, results };
  }

  async count(spec: QuerySpec): Promise<number> {
    const results = await this.query(spec);
    return results.length;
  }

  async exists(id: string): Promise<boolean> {
    return this.store.has(id);
  }

  /**
   * Get repository metadata
   */
  getMetadata(): RepositoryMetadata {
    const storageUsed = JSON.stringify(Array.from(this.store.entries())).length;

    return {
      name: 'MemoryRepository',
      version: '1.0.0',
      capabilities: {
        transactions: false,
        fullTextSearch: true,
        vectorSearch: false,
        offline: true,
        sync: false,
      },
      storage: {
        used: storageUsed,
        available: Infinity,
      },
    };
  }

  /**
   * Clear all data (useful for testing)
   */
  clear(): void {
    this.store.clear();
  }

  // =============================================================================
  // PRIVATE HELPER METHODS
  // =============================================================================

  private evaluateCondition(record: any, condition: FilterCondition): boolean {
    const value = this.getNestedValue(record, condition.field);

    switch (condition.operator) {
      case 'eq':
        return value === condition.value;
      case 'ne':
        return value !== condition.value;
      case 'gt':
        return value > condition.value;
      case 'gte':
        return value >= condition.value;
      case 'lt':
        return value < condition.value;
      case 'lte':
        return value <= condition.value;
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(value);
      case 'nin':
        return Array.isArray(condition.value) && !condition.value.includes(value);
      case 'contains':
        return typeof value === 'string' && value.includes(condition.value);
      case 'startsWith':
        return typeof value === 'string' && value.startsWith(condition.value);
      case 'endsWith':
        return typeof value === 'string' && value.endsWith(condition.value);
      default:
        return false;
    }
  }

  private getNestedValue(obj: any, path: string): any {
    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current == null) return undefined;
      current = current[part];
    }

    return current;
  }

  private applyPatchOp(record: any, op: PatchOperation): any {
    const pathParts = op.path.split('.');
    const lastPart = pathParts[pathParts.length - 1];
    const parentPath = pathParts.slice(0, -1);

    // Get parent object
    let parent = record;
    for (const part of parentPath) {
      if (parent[part] == null) {
        parent[part] = {};
      }
      parent = parent[part];
    }

    // Apply operation
    switch (op.op) {
      case 'replace':
        parent[lastPart] = op.value;
        break;

      case 'add':
        parent[lastPart] = op.value;
        break;

      case 'remove':
        delete parent[lastPart];
        break;

      case 'append':
        if (!Array.isArray(parent[lastPart])) {
          parent[lastPart] = [];
        }
        parent[lastPart].push(op.value);
        break;
    }

    return record;
  }
}
